/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/react-css
 * @description Renders the vanilla CSS single-domain adapter detail view for
 * the React CSS variant. The page surfaces current provider health, available
 * providers, and inline configuration for one adapter domain.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import type { JSX } from "react";
import { useId, useState } from "react";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterDomainIcon } from "../components/AdapterDomainIcon";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { useAdapterStatus, useAdapters } from "../hooks";
import { cx } from "../lib/cx";
import { PageErrorBoundary } from "./PageErrorBoundary";

/**
 * Props for rendering the React CSS single-domain adapter detail page.
 *
 * @property domain Adapter domain whose provider details should be displayed.
 * @property onBack Optional callback for returning to the dashboard view.
 * @property onConfigure Optional callback used to persist submitted provider configuration.
 * @property initialConfig Existing configuration value used to hydrate the inline form.
 * @property className Optional class name merged into the page container.
 */
export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  initialConfig?: DomainAdapterConfig;
  className?: string;
}

/**
 * Renders the React CSS adapter detail page for one adapter domain.
 *
 * @param props Page props including the selected domain and optional back-navigation handler.
 * @returns The detail view with provider health, available providers, and configuration affordances.
 */
export function AdapterDetailPage(props: AdapterDetailPageProps): JSX.Element {
  return (
    <PageErrorBoundary
      className={props.className}
      title={`${DOMAIN_LABELS[props.domain]} adapter failed to load`}
    >
      <AdapterDetailPageContent {...props} />
    </PageErrorBoundary>
  );
}

function AdapterDetailPageContent({
  domain,
  onBack,
  onConfigure,
  initialConfig,
  className = "",
}: AdapterDetailPageProps): JSX.Element {
  const { isLoading } = useAdapters();
  const status = useAdapterStatus(domain);
  const [isEditing, setIsEditing] = useState(false);
  const providers = getProvidersForDomain(domain);

  const label = DOMAIN_LABELS[domain];
  const desc = DOMAIN_DESCRIPTIONS[domain];
  // useId() produces a stable, per-instance id so multiple detail pages for the
  // same domain do not emit duplicate aria-labelledby targets.
  const latencyLabelId = useId();

  if (isLoading) {
    return (
      <div
        className={cx(
          "gn-adapters-page",
          "gn-adapters-page--narrow",
          "gn-adapters-page--loading",
          className,
        )}
      >
        <div
          aria-busy="true"
          aria-label={`Loading ${label} adapter`}
          className="gn-adapters-loading"
          role="status"
        >
          <div className="gn-adapters-skeleton gn-adapters-skeleton--subtitle" />
          <div className="gn-adapters-skeleton gn-adapters-skeleton--hero" />
          <div className="gn-adapters-skeleton gn-adapters-skeleton--panel" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx("gn-adapters-page", "gn-adapters-page--narrow", className)}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="gn-adapters-back-button"
        >
          <span>←</span> Back to Adapters
        </button>
      )}

      <div className="gn-adapters-detail__header">
        <div className="gn-adapters-detail__icon">
          <AdapterDomainIcon domain={domain} />
        </div>
        <div className="gn-adapters-detail__body">
          <div className="gn-adapters-detail__title-row">
            <h1 className="gn-adapters-page__title">{label}</h1>
            <AdapterStatusBadge status={status.status} />
          </div>
          <p className="gn-adapters-page__subtitle">{desc}</p>
        </div>
      </div>

      <div className="gn-adapters-summary-card">
        <h2 className="gn-adapters-summary__label">Current Status</h2>
        <div className="gn-adapters-detail__status-grid">
          <div>
            <p className="gn-adapters-detail__label">Provider</p>
            <p className="gn-adapters-detail__value">
              {status.provider !== "none" ? status.provider : "None"}
            </p>
          </div>
          <div>
            <p className="gn-adapters-detail__label">Status</p>
            <p className="gn-adapters-detail__value">
              <AdapterStatusBadge status={status.status} />
            </p>
          </div>
          <div>
            <p className="gn-adapters-detail__label">Last Checked</p>
            <p className="gn-adapters-detail__value">
              {status.lastCheckedAt
                ? new Date(status.lastCheckedAt).toLocaleTimeString()
                : "Not checked"}
            </p>
          </div>
        </div>
        {status.error && (
          <div
            className="gn-adapters-alert gn-adapters-alert--error gn-adapters-alert--spaced"
            role="alert"
          >
            <strong>Error:</strong> {status.error}
          </div>
        )}
        {status.latency !== undefined && (
          <div className="gn-adapters-detail__latency">
            <p className="gn-adapters-detail__label" id={latencyLabelId}>
              Latency
            </p>
            <meter
              aria-labelledby={latencyLabelId}
              aria-valuetext={`${status.latency} milliseconds`}
              className={cx(
                "gn-adapters-detail__meter",
                status.latency < 100
                  ? "gn-adapters-detail__meter--fast"
                  : status.latency < 500
                    ? "gn-adapters-detail__meter--medium"
                    : "gn-adapters-detail__meter--slow",
              )}
              max={1000}
              min={0}
              value={Math.min(1000, status.latency)}
            />
            <p className="gn-adapters-detail__value gn-adapters-detail__value--subtle">
              {status.latency}ms
            </p>
          </div>
        )}
      </div>

      <div className="gn-adapters-summary-card">
        <div className="gn-adapters-detail__section-header">
          <h2 className="gn-adapters-summary__label">Available Providers</h2>
          <span className="gn-adapters-detail__count">
            {providers.length} providers
          </span>
        </div>
        <div className="gn-adapters-provider-list">
          {providers.map((p) => (
            <div key={p.id} className="gn-adapters-provider-list__item">
              <div>
                <p className="gn-adapters-detail__value gn-adapters-detail__value--strong">
                  {p.name}
                </p>
                <p className="gn-adapters-detail__label">{p.description}</p>
              </div>
              <span
                className={cx(
                  "gn-adapters-tier-badge",
                  `gn-adapters-tier-badge--${p.tier}`,
                )}
              >
                {p.tier}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        {isEditing ? (
          <AdapterConfigForm
            domain={domain}
            initialConfig={initialConfig}
            onSave={async (config) => {
              await onConfigure?.(domain, config);
              setIsEditing(false);
            }}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="gn-adapters-card gn-adapters-card--action"
          >
            Configure {label} Adapter
          </button>
        )}
      </div>
    </div>
  );
}
